/**
 * Attachment Thumbnail Component
 *
 * Displays a thumbnail preview of an attachment with lazy loading.
 * Falls back to an icon if thumbnail is not available.
 */

import { useState, useRef, useEffect } from 'react'
import { FileText, FileImage, File, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { API_ROUTES } from '@/config/constants'
import { tokenStorage } from '@/lib/api'

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface AttachmentThumbnailProps {
  studyId: string
  attachmentId: string
  mimeType: string | null
  fileName: string
  className?: string
}

type ThumbnailState = 'idle' | 'loading' | 'loaded' | 'error'

export function AttachmentThumbnail({
  studyId,
  attachmentId,
  mimeType,
  fileName,
  className,
}: AttachmentThumbnailProps) {
  const [state, setState] = useState<ThumbnailState>('idle')
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Lazy loading with Intersection Observer
  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '50px' } // Preload when close to viewport
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  /**
   * Solo las imagenes tienen miniatura.
   *
   * El servidor genera miniatura de imagenes y de nada mas: para un PDF
   * devuelve 404, correctamente. Pero se le pedia igual, asi que cada apertura
   * del visor con una orden medida adjunta dejaba un 404 en el log del
   * servidor y un error rojo en la consola del navegador. La pantalla ya
   * termina ensenando el icono del tipo de archivo, que es lo que se queria:
   * se llega ahi sin pedir antes algo que no existe.
   */
  const admiteMiniatura = mimeType?.startsWith('image/') ?? false

  // Start loading when visible
  useEffect(() => {
    if (!isVisible || state !== 'idle') return

    setState(admiteMiniatura ? 'loading' : 'error')
  }, [isVisible, state, admiteMiniatura])

  // Build thumbnail URL with auth token
  const token = tokenStorage.getAccessToken()
  const thumbnailUrl = `${API_URL}${API_ROUTES.ATTACHMENTS.THUMBNAIL(studyId, attachmentId)}${token ? `?token=${token}` : ''}`

  // Fallback icon based on mime type
  const FallbackIcon = mimeType?.startsWith('image/')
    ? FileImage
    : mimeType === 'application/pdf'
      ? FileText
      : File

  // Icon color based on type
  const iconColor = mimeType?.startsWith('image/')
    ? 'text-blue-400'
    : mimeType === 'application/pdf'
      ? 'text-red-400'
      : 'text-slate-400'

  // Get file extension for display
  const fileExt = fileName.split('.').pop()?.toUpperCase() || ''

  return (
    <div
      ref={ref}
      className={cn(
        'relative w-full h-full flex items-center justify-center bg-slate-800 overflow-hidden',
        className
      )}
    >
      {/* Loading state */}
      {state === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
          <Loader2 className="h-4 w-4 text-slate-500 animate-spin" />
        </div>
      )}

      {/* Error state - show fallback icon */}
      {state === 'error' && (
        <div className="flex flex-col items-center justify-center gap-0.5 p-1">
          <FallbackIcon className={cn('h-5 w-5', iconColor)} />
          {fileExt && (
            <span className="text-[7px] text-slate-500 text-center truncate max-w-full font-medium">
              {fileExt}
            </span>
          )}
        </div>
      )}

      {/* Thumbnail image */}
      {isVisible && admiteMiniatura && state !== 'error' && (
        <img
          src={thumbnailUrl}
          alt={fileName}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            state === 'loaded' ? 'opacity-100' : 'opacity-0'
          )}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
        />
      )}
    </div>
  )
}
